import { CookieService } from "@Front/Services/CookieService/CookieService";
import { getActiveSessionFamily } from "@Front/Utils/lecoffreActiveSessionFamily";

/** Single source of truth for LeCoffre session cookie names (front read/write). */
export const LECOFFRE_NOTARY_ACCESS_TOKEN_COOKIE_NAME = "leCoffreNotaryAccessToken";
export const LECOFFRE_NOTARY_REFRESH_TOKEN_COOKIE_NAME = "leCoffreNotaryRefreshToken";
export const LECOFFRE_CUSTOMER_ACCESS_TOKEN_COOKIE_NAME = "leCoffreCustomerAccessToken";
export const LECOFFRE_CUSTOMER_REFRESH_TOKEN_COOKIE_NAME = "leCoffreCustomerRefreshToken";
export const LECOFFRE_THIRDPARTY_ACCESS_TOKEN_COOKIE_NAME = "leCoffreThirdPartyAccessToken";
export const LECOFFRE_THIRDPARTY_REFRESH_TOKEN_COOKIE_NAME = "leCoffreThirdPartyRefreshToken";
export const LECOFFRE_ACCESS_TOKEN_COOKIE_NAME = LECOFFRE_NOTARY_ACCESS_TOKEN_COOKIE_NAME;
export const LECOFFRE_REFRESH_TOKEN_COOKIE_NAME = LECOFFRE_NOTARY_REFRESH_TOKEN_COOKIE_NAME;

/** All first-party LeCoffre session cookie names (auth wipe must not clear unrelated cookies). */
export const LECOFFRE_SESSION_COOKIE_NAMES = [
	LECOFFRE_NOTARY_ACCESS_TOKEN_COOKIE_NAME,
	LECOFFRE_NOTARY_REFRESH_TOKEN_COOKIE_NAME,
	LECOFFRE_CUSTOMER_ACCESS_TOKEN_COOKIE_NAME,
	LECOFFRE_CUSTOMER_REFRESH_TOKEN_COOKIE_NAME,
	LECOFFRE_THIRDPARTY_ACCESS_TOKEN_COOKIE_NAME,
	LECOFFRE_THIRDPARTY_REFRESH_TOKEN_COOKIE_NAME,
] as const;

export type LeCoffreAuthContext = "notary" | "customer" | "thirdparty";
type EndpointAuthContextOverrideRule = {
	pattern: string;
	context: LeCoffreAuthContext;
};

function readNonEmptyCookie(name: string): string | null {
	if (typeof window === "undefined") {
		return null;
	}
	const raw = CookieService.getInstance().getCookie(name);
	return typeof raw === "string" && raw.length > 0 ? raw : null;
}

function resolveFirstAvailableAccessTokenCookie(): string | null {
	return (
		getLeCoffreNotaryAccessTokenCookie() ??
		getLeCoffreCustomerAccessTokenCookie() ??
		getLeCoffreThirdPartyAccessTokenCookie()
	);
}

function resolveFirstAvailableRefreshTokenCookie(): string | null {
	return (
		getLeCoffreNotaryRefreshTokenCookie() ??
		getLeCoffreCustomerRefreshTokenCookie() ??
		getLeCoffreThirdPartyRefreshTokenCookie()
	);
}

/** Raw access JWT from active session cookie (browser only). */
export function getLeCoffreAccessTokenCookie(): string | null {
	return resolveFirstAvailableAccessTokenCookie();
}

/** Raw refresh JWT from active session cookie (browser only). */
export function getLeCoffreRefreshTokenCookie(): string | null {
	return resolveFirstAvailableRefreshTokenCookie();
}

export function getLeCoffreNotaryAccessTokenCookie(): string | null {
	return readNonEmptyCookie(LECOFFRE_NOTARY_ACCESS_TOKEN_COOKIE_NAME);
}

export function getLeCoffreNotaryRefreshTokenCookie(): string | null {
	return readNonEmptyCookie(LECOFFRE_NOTARY_REFRESH_TOKEN_COOKIE_NAME);
}

export function getLeCoffreCustomerAccessTokenCookie(): string | null {
	return readNonEmptyCookie(LECOFFRE_CUSTOMER_ACCESS_TOKEN_COOKIE_NAME);
}

export function getLeCoffreCustomerRefreshTokenCookie(): string | null {
	return readNonEmptyCookie(LECOFFRE_CUSTOMER_REFRESH_TOKEN_COOKIE_NAME);
}

export function getLeCoffreThirdPartyAccessTokenCookie(): string | null {
	return readNonEmptyCookie(LECOFFRE_THIRDPARTY_ACCESS_TOKEN_COOKIE_NAME);
}

export function getLeCoffreThirdPartyRefreshTokenCookie(): string | null {
	return readNonEmptyCookie(LECOFFRE_THIRDPARTY_REFRESH_TOKEN_COOKIE_NAME);
}

function normalizeEndpoint(endpoint: string | null | undefined): string {
	if (typeof endpoint !== "string") {
		return "";
	}
	const trimmed = endpoint.trim();
	if (!trimmed) {
		return "";
	}
	const normalized = trimmed.toLowerCase();
	const isAbsoluteUrl = normalized.startsWith("http://") || normalized.startsWith("https://");
	const parseInput = isAbsoluteUrl ? normalized : normalized.startsWith("/") ? normalized : `/${normalized}`;
	try {
		const parsed = new URL(parseInput, "https://lecoffre.local");
		const pathname = parsed.pathname.toLowerCase();
		const search = parsed.search.toLowerCase();
		return `${pathname}${search}`;
	} catch {
		return parseInput;
	}
}

function matchesEndpointNamespace(normalizedEndpoint: string, namespace: "notary" | "idnot" | "customer" | "third-party"): boolean {
	const root = `/${namespace}`;
	return normalizedEndpoint === root || normalizedEndpoint.includes(`${root}/`);
}

/**
 * Central registry for historical endpoints whose auth context differs from their URL namespace.
 * Keep this list explicit and minimal: each entry must be validated by a real production flow.
 */
const ENDPOINT_AUTH_CONTEXT_OVERRIDES: EndpointAuthContextOverrideRule[] = [
	{
		pattern: "/customer/notes",
		context: "notary",
	},
];

const ACTIVE_SESSION_TOKEN_ENDPOINT_PATTERNS: string[] = [
	"/auth/logout",
	"/customer/folders",
	"/customer/documents",
	/** Third parties (and invited flows) call customer-namespaced file APIs with the active third-party JWT. */
	"/customer/files",
	"/customer/files-notary",
	"/customer/office",
	/** Notes (POST/GET …/customer/notes and …/customer/notes/customer/:id/folders/:id) use Customer API with third-party JWT when applicable. */
	"/customer/notes",
	"/notary/my-permissions",
	"/notary/permissions-support/client-matrix-view",
	"/notary/documents",
	"/notary/documents_notary",
	"/notary/files",
	"/notary/files-notary",
	"/notary/third-parties",
	"/notary/folders/autocomplete/third-parties",
];

function resolveEndpointAuthContextOverride(normalizedEndpoint: string): LeCoffreAuthContext | null {
	const rule = ENDPOINT_AUTH_CONTEXT_OVERRIDES.find((entry) => normalizedEndpoint.includes(entry.pattern));
	return rule?.context ?? null;
}

export function shouldUseActiveSessionTokenForEndpoint(endpoint: string | null | undefined): boolean {
	const normalized = normalizeEndpoint(endpoint);
	if (!normalized) {
		return false;
	}
	return ACTIVE_SESSION_TOKEN_ENDPOINT_PATTERNS.some((pattern) => normalized.includes(pattern));
}

export function resolveLeCoffreAuthContextFromEndpoint(endpoint: string | null | undefined): LeCoffreAuthContext | null {
	const normalized = normalizeEndpoint(endpoint);
	if (!normalized) {
		return null;
	}
	const overrideContext = resolveEndpointAuthContextOverride(normalized);
	if (overrideContext) {
		return overrideContext;
	}
	if (matchesEndpointNamespace(normalized, "notary") || matchesEndpointNamespace(normalized, "idnot")) {
		return "notary";
	}
	if (matchesEndpointNamespace(normalized, "customer")) {
		return "customer";
	}
	if (normalized.includes("/id360/customers/")) {
		return "customer";
	}
	if (matchesEndpointNamespace(normalized, "third-party")) {
		return "thirdparty";
	}
	return null;
}

export function getLeCoffreAccessTokenCookieForContext(context: LeCoffreAuthContext): string | null {
	if (context === "notary") return getLeCoffreNotaryAccessTokenCookie();
	if (context === "customer") return getLeCoffreCustomerAccessTokenCookie();
	return getLeCoffreThirdPartyAccessTokenCookie();
}

function waitForCookieRetry(delayMs: number): Promise<void> {
	return new Promise((resolve) => setTimeout(resolve, delayMs));
}

export async function assertAccessCookieForContext(
	context: LeCoffreAuthContext,
	retryDelayMs: number = 200,
): Promise<string> {
	const cookie = getLeCoffreAccessTokenCookieForContext(context);
	if (cookie) {
		return cookie;
	}
	await waitForCookieRetry(retryDelayMs);
	const retryCookie = getLeCoffreAccessTokenCookieForContext(context);
	if (retryCookie) {
		return retryCookie;
	}
	throw new Error(`ACCESS_COOKIE_MISSING_${context.toUpperCase()}`);
}

export function getLeCoffreRefreshTokenCookieForContext(context: LeCoffreAuthContext): string | null {
	if (context === "notary") return getLeCoffreNotaryRefreshTokenCookie();
	if (context === "customer") return getLeCoffreCustomerRefreshTokenCookie();
	return getLeCoffreThirdPartyRefreshTokenCookie();
}

function resolveActiveAuthContextFromCookiesOnly(): LeCoffreAuthContext | null {
	const activeFamily = getActiveSessionFamily();
	if (activeFamily && getLeCoffreAccessTokenCookieForContext(activeFamily)) {
		return activeFamily;
	}
	if (getLeCoffreNotaryAccessTokenCookie()) {
		return "notary";
	}
	if (getLeCoffreCustomerAccessTokenCookie()) {
		return "customer";
	}
	if (getLeCoffreThirdPartyAccessTokenCookie()) {
		return "thirdparty";
	}
	return null;
}

export function getLeCoffreRefreshTokenCookieForEndpoint(endpoint: string | null | undefined): string | null {
	if (shouldUseActiveSessionTokenForEndpoint(endpoint)) {
		const activeContext = resolveActiveAuthContextFromCookiesOnly();
		return activeContext ? getLeCoffreRefreshTokenCookieForContext(activeContext) : null;
	}
	const context = resolveLeCoffreAuthContextFromEndpoint(endpoint);
	if (!context) {
		return getLeCoffreNotaryRefreshTokenCookie();
	}
	return getLeCoffreRefreshTokenCookieForContext(context);
}
